/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type Maybe from 'graphql/tsutils/Maybe';

import type { Theme } from '@coursera/cds-core';
import { Grid, PageGridContainer, Typography, breakpoints, typography2 } from '@coursera/cds-core';

import { TestimonialsModule } from 'bundles/cds-labs';
import Markdown from 'bundles/cms/components/Markdown';
import type { TestimonialsFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SocialProofItem from 'bundles/front-page/components/modules/SocialProofItem';
import { getLOHPTestimonialsHeader } from 'bundles/front-page/constants/defaults';

const styles = {
  socialProofContainer: () => css`
    display: block;
    margin-top: 100px;
    position: relative;
    width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;

    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-800) 0 var(--cds-spacing-800);
    }
  `,
  text: css`
    margin: 0 auto 50px;
    max-width: 800px;
    text-align: center;
    width: 100%;
  `,
  title: () => css`
    font-weight: 600;
    font-family: 'Source Sans Pro', Arial, sans-serif;
    color: var(--cds-color-grey-975);
    font-size: 44px;
    line-height: 52px;
    margin: 10px 20px 14px;
    ${breakpoints.down('sm')} {
      font-size: 36px;
      line-height: 48px;
    }
    ${breakpoints.down('xs')} {
      font-size: 28px;
      line-height: 36px;
    }
  `,
  subtitle: () => css`
    color: var(--cds-color-grey-975);
    font-size: 28px;
    line-height: 36px;
    ${breakpoints.down('sm')} {
      font-size: 28px;
      line-height: 36px;
    }
    ${breakpoints.down('xs')} {
      font-size: 16px;
      line-height: 21px;
    }
  `,
  tesimonialsSliderPageGridContainer: css`
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-50) !important;
    }
  `,
  textContainer: css`
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-200);
    }
  `,
  mobileText: css`
    ${breakpoints.down('xs')} {
      margin: 0 auto var(--cds-spacing-300);

      .rc-Markdown {
        h2 {
          ${typography2.titleSmall}
          margin-bottom: var(--cds-spacing-100);
        }

        p {
          ${typography2.bodySecondary}
        }
      }
    }
  `,
  carouselContainer: css`
    margin-bottom: 100px;

    /* Increase card width between tablet and mobile widths */
    @media (min-width: ${breakpoints.values.sm}px) and (max-width: ${breakpoints.values.md}px) {
      .rc-SocialProofItemRedesign > div {
        max-width: 520px;
      }
    }
  `,
};

const overrideStyles = {
  markdown: (theme: Theme) => css`
    ${css(styles.text)}
    .rc-Markdown {
      h2 {
        ${theme.typography.d1semibold}
        ${css(styles.title())}
      }

      p {
        ${theme.typography.d2}
        ${css(styles.subtitle())}
      }
    }
  `,
};

type Props = {
  testimonials: TestimonialsFieldsFragment[];
  title?: Maybe<string>;
};

const renderSocialProofItems = (testimonials: Props['testimonials']) => {
  return testimonials
    .map((testimonial) => {
      const { authorName, authorTitle, authorOrganization, text, image } = testimonial;

      if (!authorName || !text || !image || !image.url) {
        return null;
      }

      return (
        <SocialProofItem
          key={authorName}
          name={authorName}
          title={authorTitle ?? ''}
          subtitle={authorOrganization ?? ''}
          body={text}
          rebrandImageUrl={image && image.url}
        />
      );
    })
    .filter(Boolean);
};

const SocialProofContainerCMS = ({ title, testimonials }: Props) => {
  return (
    <section className="rc-SocialProofContainerRedesign" css={styles.socialProofContainer}>
      <PageGridContainer css={styles.tesimonialsSliderPageGridContainer}>
        <Grid item xs={12}>
          <div css={styles.textContainer}>
            {title ? (
              <div css={[overrideStyles.markdown, styles.mobileText]}>
                <Markdown source={title} />
              </div>
            ) : (
              <div css={[styles.text, styles.mobileText]}>
                <Typography variant="d1semibold" component="h2" css={styles.title}>
                  {getLOHPTestimonialsHeader().title}
                </Typography>
                <Typography variant="d2" component="p" css={styles.subtitle}>
                  {getLOHPTestimonialsHeader().subtitle}
                </Typography>
              </div>
            )}
          </div>
          <div css={styles.carouselContainer}>
            <TestimonialsModule testimonialItems={renderSocialProofItems(testimonials)} numTestimonialsToDisplay={3} />
          </div>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default SocialProofContainerCMS;
